import { Form, SubmitFormButton } from '@calendly/form'
import { object, string } from '@calendly/form/validation'
import { I18n } from '@calendly/i18n'
import { Banner } from '@calendly/ui/components/banner'
import { Button } from '@calendly/ui/components/button'
import { Loader } from '@calendly/ui/components/loader'
import { PageHeader } from '@calendly/ui/components/page-header'
import { BodyText } from '@calendly/ui/components/typography'
import { useDeviceType } from '@calendly/ui/hooks/use-device-type'
import { useSelector } from 'react-redux'

import { useEnforceUpgrade } from 'components/billing/useEnforceUpgrade'
import { PageContainer } from 'components/page_elements'
import { isRestrictedFeatureSelector } from 'features/billing'

import { useFetchContactUserSettingsQuery, useUpdateContactUserSettingsMutation } from './api'
import { FormContent } from './form_content'
import { BannerInnerContainer, FormFooter, SecurityShieldIcon } from './styles'

const validationSchema = object({
  enrichmentEnabled: string().required().oneOf(['enabled', 'disabled'])
})

const normalizeResponseData = (data) => ({
  enrichmentEnabled: data.enrichmentEnabled ? 'enabled' : 'disabled'
})

const UpgradeBanner = () => {
  const isPhone = useDeviceType('isPhone')
  const enforceUpgrade = useEnforceUpgrade('contact_enrichment', {
    skipPreface: true,
    paywall: 'contact_enrichment_upgrade_btn_click',
    type: 'inline'
  })

  return (
    <Banner
      spacingTop={24}
      spacingBottom={48}
      type="primary"
      icon={SecurityShieldIcon}
      footer={(
        isPhone && (
          <Button onClick={enforceUpgrade}>
            {I18n.t('publisher.account_settings.contact_enrichment.upgrade_banner.button')}
          </Button>
        )
      )}
    >
      <BannerInnerContainer>
        {I18n.t('publisher.account_settings.contact_enrichment.upgrade_banner.title')}
        {!isPhone && (
          <Button onClick={enforceUpgrade}>
            {I18n.t('publisher.account_settings.contact_enrichment.upgrade_banner.button')}
          </Button>
        )}
      </BannerInnerContainer>
    </Banner>
  )
}

export const Page = () => {
  const { data: settings, isLoading, isError } = useFetchContactUserSettingsQuery()
  const [update] = useUpdateContactUserSettingsMutation()
  const upgradeIsRequired = useSelector((state) => isRestrictedFeatureSelector(state, { featureKey: 'contact_enrichment' }))

  const onSubmit = ({ enrichmentEnabled }, { resetForm }) => {
    update({ settings: { enrichmentEnabled: enrichmentEnabled === 'enabled' } })
    resetForm({ values: { enrichmentEnabled } })
  }

  return (
    <>
      <PageHeader
        eyebrow={I18n.t('publisher.personal_settings.navigation.account_details')}
        title={I18n.t('publisher.account_settings.navigation.contact_enrichment')}
      />
      {upgradeIsRequired && <UpgradeBanner />}
      <PageContainer>
        {isLoading ? <Loader /> : (
          <>
            <BodyText weight="bold">{I18n.t('publisher.account_settings.contact_enrichment.short_description')}</BodyText>
            <BodyText>{I18n.t('publisher.account_settings.contact_enrichment.description')}</BodyText>
            {!isError && (
              <Form
                initialValues={normalizeResponseData(settings)}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
              >
                <FormContent>
                  {({ formChanged, disabledByPayWall }) => (
                    <FormFooter>
                      <Button type="reset" decoration="secondary-outline" disabled={!formChanged || disabledByPayWall}>
                        {I18n.t('publisher.action.cancel')}
                      </Button>
                      <SubmitFormButton disabled={!formChanged || disabledByPayWall}>
                        {I18n.t('publisher.action.save')}
                      </SubmitFormButton>
                    </FormFooter>
                  )}
                </FormContent>
              </Form>
            )}
          </>
        )}
      </PageContainer>
    </>
  )
}
